import React, { useState } from 'react';
import { Clock, Building, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import AptiviewLoader from '../../../common/AptiviewLoader/AptiviewLoader';
import { endInterview } from '../../../../api/client';
import DynamicBasicDialog from '../../component/DynamicBasicDialog';

const LiveInterviewProgress = ({ liveInterviews, liveInterviewsLoading, navigate, onInterviewEnded }) => {
    const [endingInterview, setEndingInterview] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, interviewId: null, interviewTitle: null });
    const [error, setError] = useState(null);
    if (liveInterviewsLoading) {
        return (
            <div className="flex justify-center py-8">
                <AptiviewLoader size="sm" message="Loading live interviews..." />
            </div>
        );
    }

    if (!liveInterviews || liveInterviews.length === 0) {
        return null;
    }

    const handleEndInterviewClick = (interviewId, interviewTitle) => {
        setConfirmDialog({
            open: true,
            interviewId: interviewId,
            interviewTitle: interviewTitle
        });
        setError(null);
    };

    const handleConfirmEndInterview = async () => {
        if (!confirmDialog.interviewId) return;

        setEndingInterview(confirmDialog.interviewId);
        setConfirmDialog({ open: false, interviewId: null, interviewTitle: null });
        setError(null);

        try {
            await endInterview(confirmDialog.interviewId);
            
            // Refresh the live interviews list
            if (onInterviewEnded) {
                onInterviewEnded();
            }
        } catch (err) {
            console.error('Error ending interview:', err);
            setError(err.response?.data?.message || err.message || 'Failed to end interview. Please try again.');
            setEndingInterview(null);
        }
    };

    const handleCancelEndInterview = () => {
        setConfirmDialog({ open: false, interviewId: null, interviewTitle: null });
        setError(null);
    };

    return (
        <div className="w-full">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="relative">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="absolute inset-0 w-3 h-3 bg-red-500 rounded-full animate-ping opacity-75"></div>
                </div>
                <h2 className="text-xl font-semibold text-gray-800">
                    Live Interview Progress
                </h2>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
                {liveInterviews.map((interview) => (
                    <div
                        key={interview.interview_id}
                        className="bg-gradient-to-br from-red-50 via-pink-50 to-orange-50 rounded-xl border-2 border-red-200/60 p-6 hover:shadow-xl hover:border-red-300 hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden"
                    >
                        {/* Animated background pulse effect */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-red-200/20 rounded-full -mr-16 -mt-16 animate-pulse"></div>
                        
                        <div className="relative z-10">
                            {/* Header Section */}
                            <div className="flex items-start justify-between gap-4 mb-5">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                            <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">
                                                Live
                                            </span>
                                        </div>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-1.5 truncate">
                                        {interview.job_title || 'Interview'}
                                    </h3>
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <Building size={14} className="text-gray-500" />
                                        <span className="truncate">{interview.company_name || 'Company'}</span>
                                    </div>
                                </div>
                                
                                {/* Time Remaining Badge */}
                                <div className="flex-shrink-0 bg-red-100 rounded-lg px-3 py-2 border border-red-200">
                                    <div className="flex items-center gap-1.5">
                                        <Clock size={14} className="text-red-600" />
                                        <div className="text-center">
                                            <div className="text-lg font-bold text-red-700 leading-tight">
                                                {interview.remaining_minutes || 0}:{String((interview.remaining_seconds || 0) % 60).padStart(2, '0')}
                                            </div>
                                            <div className="text-xs text-red-600/80 font-medium">
                                                Remaining
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Progress Section */}
                            <div className="mb-5">
                                <div className="flex justify-between items-center mb-2.5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            Progress
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            ({interview.questions_answered || 0}/{interview.total_questions || 0} questions)
                                        </span>
                                    </div>
                                    <span className="text-sm font-bold text-red-600">
                                        {interview.progress_percentage?.toFixed(1) || 0}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden shadow-inner">
                                    <div 
                                        className="h-full bg-gradient-to-r from-red-500 via-pink-500 to-orange-500 rounded-full transition-all duration-500 ease-out relative"
                                        style={{ width: `${interview.progress_percentage || 0}%` }}
                                    >
                                        <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                                    </div>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                {/* Current Question */}
                                <div className="bg-white/80 rounded-lg p-3 border border-red-100/50">
                                    <div className="text-xs text-gray-500 mb-1">Current Question</div>
                                    <div className="flex items-center gap-1.5">
                                        <CheckCircle2 size={14} className="text-red-500" />
                                        <span className="text-sm font-semibold text-gray-800">
                                            {interview.current_question || 0}/{interview.total_questions || 0}
                                        </span>
                                    </div>
                                </div>

                                {/* Estimated End Time */}
                                <div className="bg-white/80 rounded-lg p-3 border border-red-100/50">
                                    <div className="text-xs text-gray-500 mb-1">Estimated End</div>
                                    <div className="flex items-center gap-1.5">
                                        <Clock size={14} className="text-gray-500" />
                                        <span className="text-sm font-semibold text-gray-800">
                                            {interview.estimated_end_time 
                                                ? new Date(interview.estimated_end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                : 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* End Interview Button */}
                            <button
                                onClick={() => handleEndInterviewClick(interview.interview_id, interview.job_title)}
                                disabled={endingInterview === interview.interview_id}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg font-medium text-sm"
                            >
                                {endingInterview === interview.interview_id ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Ending Interview...
                                    </>
                                ) : (
                                    <>
                                        <XCircle size={16} />
                                        End Interview
                                    </>
                                )}
                            </button>

                            {/* Error Message */}
                            {error && endingInterview === interview.interview_id && (
                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-xs text-red-600">{error}</p>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Confirmation Dialog */}
            <DynamicBasicDialog
                open={confirmDialog.open}
                onClose={handleCancelEndInterview}
                onConfirm={handleConfirmEndInterview}
                title="End Interview Confirmation"
                content={
                    confirmDialog.interviewTitle
                        ? `Are you sure you want to end the interview for "${confirmDialog.interviewTitle}"? This will remove the Redis TTL, mark the interview as completed, and generate final feedback. This action cannot be undone.`
                        : "Are you sure you want to end this interview? This will remove the Redis TTL, mark the interview as completed, and generate final feedback. This action cannot be undone."
                }
            />
        </div>
    );
};

export default LiveInterviewProgress;